import { useEffect, useState } from 'react';
import { call } from '@edil/api-client';
import { Alert, Button, Dialog, buttonClass } from '@edil/ui';
import { useApp } from '@/components/app-provider';
import { money } from '@/lib/format';
import { useInterval } from '@/lib/hooks';
import { Link } from '@/router';

type Check = Awaited<ReturnType<typeof load>>;
const load = (api: ReturnType<typeof useApp>['api']) => call(api.GET('/v1/me/reality-check'));

/**
 * Reality check (BUILD_PLAN §5.9): after each interval of play, time played and net result. Only
 * while the setting rg.enabled is on, and with a single button, so nobody taps a break by mistake.
 */
export function RealityCheck() {
  const { api, t, signedIn, me } = useApp();
  const on = signedIn && Boolean(me?.rg_enabled);
  const [check, setCheck] = useState<Check | null>(null);
  const poll = () => {
    if (!on) return;
    load(api)
      .then((c) => setCheck(c.due ? c : null))
      .catch(() => {});
  };
  useEffect(poll, [on]); // eslint-disable-line react-hooks/exhaustive-deps
  useInterval(poll, on ? 60_000 : null);

  const ack = async () => {
    setCheck(null);
    await call(api.POST('/v1/me/reality-check/ack', {})).catch(() => {});
  };

  return (
    <Dialog
      open={on && Boolean(check)}
      onClose={() => void ack()}
      title={t('rg.reality_title')}
      description={check ? t('rg.reality_text', { minutes: check.minutes_played, staked: money(check.staked), net: money(check.net_result) }) : undefined}
      closeLabel={t('common.close')}
      footer={
        <Button onClick={() => void ack()} data-testid="keep-playing">
          {t('rg.keep_playing')}
        </Button>
      }
    />
  );
}

/** Shown on every page while a cool-off or self-exclusion is active. */
export function ExclusionBanner() {
  const { me, t } = useApp();
  if (me?.status !== 'SELF_EXCLUDED') return null;
  return (
    <Alert
      tone="warning"
      action={
        <Link href="/wallet/" className={buttonClass('secondary', 'sm')}>
          {t('wallet.withdraw')}
        </Link>
      }
    >
      {t('rg.banner')}
    </Alert>
  );
}
